<script>
import VueSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import Button from "primevue/button";
import Checkbox from "primevue/checkbox";
import InputNumber from "primevue/inputnumber";
import InputText from "primevue/inputtext";
import RadioButton from "primevue/radiobutton";
import Textarea from "primevue/textarea";
import DashboardLayout from "../layouts/DashboardLayout.vue";
import StateBlock from "../components/StateBlock.vue";
import {api} from "../services/api";

export default {
    name: "KnowledgeBaseView",
    components: {
        VueSelect,
        Button,
        Checkbox,
        InputNumber,
        InputText,
        RadioButton,
        Textarea,
        DashboardLayout,
        StateBlock,
    },
    data() {
        return {
            agents: [],
            sources: [],
            jobs: [],
            agentId: "",
            file: null,
            docTitle: "",
            expandedSourceId: null,
            ingestedPages: [],
            ingestedPageSearch: "",
            discoveredPages: [],
            discoveredPageSearch: "",
            isLoading: false,
            isLoadingJobs: false,
            isLoadingPages: false,
            isLoadingDiscoveredPages: false,
            isSavingUrl: false,
            isSavingText: false,
            isUploadingDocument: false,
            isIndexingSelectedPages: false,
            urlForm: {
                title: "",
                url: "",
                crawlMode: "all",
                customCrawlLimit: null,
                reviewBeforeIndex: false,
            },
            textForm: {
                title: "",
                content: "",
            },
        };
    },
    computed: {
        webSourceCount() {
            return this.sources.filter((source) => source.sourceType === "WebUrl").length;
        },
        totalDiscoveredPages() {
            return this.sources.reduce((total, source) => total + (source.discoveredPageCount || 0), 0);
        },
        totalIndexedPages() {
            return this.sources.reduce((total, source) => total + (source.ingestedPageCount || 0), 0);
        },
        filteredDiscoveredPages() {
            const query = this.discoveredPageSearch.trim().toLowerCase();
            if (!query) return this.discoveredPages;
            return this.discoveredPages.filter((page) => `${page.title || ""} ${page.url || ""}`.toLowerCase().includes(query));
        },
        filteredIngestedPages() {
            const query = this.ingestedPageSearch.trim().toLowerCase();
            if (!query) return this.ingestedPages;
            return this.ingestedPages.filter((page) => `${page.title || ""} ${page.url || ""}`.toLowerCase().includes(query));
        },
        selectedDiscoveredPageCount() {
            return this.discoveredPages.filter((page) => page.isSelected).length;
        },
        agentSelectOptions() {
            return this.agents.map((agent) => ({label: agent.name, value: agent.id}));
        },
    },
    methods: {
        onFileChange(event) {
            this.file = event.target.files?.[0] || null;
        },
        async loadAgents() {
            this.agents = (await api.get("/ai-agents")).data;
            await this.loadJobs();
        },
        async loadJobs() {
            this.isLoadingJobs = true;
            try {
                this.jobs = (await api.get("/knowledge-base/jobs")).data;
            } finally {
                this.isLoadingJobs = false;
            }
        },
        async loadSources() {
            this.isLoading = true;
            try {
                this.sources = this.agentId ? (await api.get(`/ai-agents/${this.agentId}/knowledge-base`)).data : [];
                this.resetExpandedSource();
                await this.loadJobs();
            } finally {
                this.isLoading = false;
            }
        },
        async addUrl() {
            this.isSavingUrl = true;
            try {
                await api.post(`/ai-agents/${this.agentId}/knowledge-base/web-url`, {
                    title: this.urlForm.title,
                    url: this.urlForm.url,
                    crawlPageLimit: this.buildCrawlPageLimit(),
                    reviewBeforeIndex: this.urlForm.reviewBeforeIndex,
                });
                Object.assign(this.urlForm, {
                    title: "",
                    url: "",
                    crawlMode: "all",
                    customCrawlLimit: null,
                    reviewBeforeIndex: false,
                });
                await this.loadSources();
            } finally {
                this.isSavingUrl = false;
            }
        },
        async addManualText() {
            this.isSavingText = true;
            try {
                await api.post(`/ai-agents/${this.agentId}/knowledge-base/manual-text`, this.textForm);
                Object.assign(this.textForm, {title: "", content: ""});
                await this.loadSources();
            } finally {
                this.isSavingText = false;
            }
        },
        async uploadDocument() {
            this.isUploadingDocument = true;
            try {
                const data = new FormData();
                data.append("title", this.docTitle);
                data.append("file", this.file);
                await api.post(`/ai-agents/${this.agentId}/knowledge-base/document`, data);
                this.file = null;
                this.docTitle = "";
                if (this.$refs.fileInput) this.$refs.fileInput.value = "";
                await this.loadSources();
            } finally {
                this.isUploadingDocument = false;
            }
        },
        async toggleSourceExpanded(source) {
            if (this.expandedSourceId === source.id) {
                this.resetExpandedSource();
                return;
            }

            this.expandedSourceId = source.id;
            this.ingestedPages = [];
            this.discoveredPages = [];
            this.ingestedPageSearch = "";
            this.discoveredPageSearch = "";
            this.isLoadingPages = true;
            this.isLoadingDiscoveredPages = source.sourceType === "WebUrl";

            try {
                const requests = [api.get(`/ai-agents/${this.agentId}/knowledge-base/${source.id}/ingested-pages`)];
                if (source.sourceType === "WebUrl") {
                    requests.push(api.get(`/ai-agents/${this.agentId}/knowledge-base/${source.id}/discovered-pages`));
                }
                const [ingestedResponse, discoveredResponse] = await Promise.all(requests);
                this.ingestedPages = ingestedResponse.data;
                this.discoveredPages = discoveredResponse?.data || [];
            } finally {
                this.isLoadingPages = false;
                this.isLoadingDiscoveredPages = false;
            }
        },
        async indexSelectedDiscoveredPages(source) {
            const selectedPageIds = this.discoveredPages.filter((page) => page.isSelected).map((page) => page.id);
            if (selectedPageIds.length === 0) return;

            this.isIndexingSelectedPages = true;
            try {
                await api.post(`/ai-agents/${this.agentId}/knowledge-base/${source.id}/index-selected`, {selectedPageIds});
                await this.loadSources();
                await this.toggleSourceExpanded(source);
            } finally {
                this.isIndexingSelectedPages = false;
            }
        },
        async deleteSource(source) {
            if (!confirm(`Delete knowledge source "${source.title}"?`)) return;
            await api.delete(`/ai-agents/${this.agentId}/knowledge-base/${source.id}`);
            await this.loadSources();
        },
        selectAllDiscoveredPages(isSelected) {
            this.filteredDiscoveredPages.forEach((page) => {
                page.isSelected = isSelected;
            });
        },
        buildCrawlPageLimit() {
            if (this.urlForm.crawlMode === "page") return 1;
            if (this.urlForm.crawlMode === "custom") return this.urlForm.customCrawlLimit || 1;
            return 0;
        },
        resetExpandedSource() {
            this.expandedSourceId = null;
            this.ingestedPages = [];
            this.discoveredPages = [];
            this.ingestedPageSearch = "";
            this.discoveredPageSearch = "";
        },
        sourceTitle(sourceId) {
            return this.sources.find((source) => source.id === sourceId)?.title || sourceId;
        },
        formatDate(value) {
            return value ? new Date(value).toLocaleString() : "";
        },
    },
    mounted() {
        this.loadAgents();
    },
};
</script>

<template>
    <DashboardLayout title="Knowledge Base" subtitle="Review crawled pages and index selected sources from one place.">
        <div class="knowledge-base-page">
            <section class="panel kb-add-panel">
                <div class="toolbar">
                    <div>
                        <h2>Add Content</h2>
                        <p class="toolbar-subtitle">Create web, text, or document sources for the selected AI Agent.</p>
                    </div>
                    <div class="form-toolbar">
                        <VueSelect
                            v-model="agentId"
                            class="agent-select"
                            :options="agentSelectOptions"
                            label="label"
                            :reduce="(option) => option.value"
                            placeholder="Select AI Agent"
                            @update:modelValue="loadSources"
                        />
                        <Button label="Refresh" severity="secondary" outlined :disabled="!agentId || isLoading" @click="loadSources" />
                    </div>
                </div>

                <div class="split form-split">
                    <form class="form-grid form-card" @submit.prevent="addUrl">
                        <div class="form-card-head">
                            <h3>Web URL</h3>
                            <span class="form-card-tag">Crawler</span>
                        </div>
                        <div class="field"><label>Title</label><InputText v-model="urlForm.title" fluid /></div>
                        <div class="field"><label>URL</label><InputText v-model="urlForm.url" type="url" required fluid /></div>
                        <div class="field">
                            <label>Crawl depth</label>
                            <div class="radio-row">
                                <div class="radio-item">
                                    <RadioButton v-model="urlForm.crawlMode" inputId="crawl-all" name="kbCrawlMode" value="all" />
                                    <label for="crawl-all">All website</label>
                                </div>
                                <div class="radio-item">
                                    <RadioButton v-model="urlForm.crawlMode" inputId="crawl-page" name="kbCrawlMode" value="page" />
                                    <label for="crawl-page">Page only</label>
                                </div>
                                <div class="radio-item">
                                    <RadioButton v-model="urlForm.crawlMode" inputId="crawl-custom" name="kbCrawlMode" value="custom" />
                                    <label for="crawl-custom">Custom</label>
                                </div>
                            </div>
                            <InputNumber v-if="urlForm.crawlMode === 'custom'" v-model="urlForm.customCrawlLimit" :min="1" :useGrouping="false" placeholder="Maximum pages" fluid />
                            <small class="muted-text">All website uses 0, page only uses 1, custom uses your page limit.</small>
                        </div>
                        <div class="checkbox-row">
                            <Checkbox v-model="urlForm.reviewBeforeIndex" inputId="reviewBeforeIndex" binary />
                            <label for="reviewBeforeIndex">Review pages before indexing</label>
                        </div>
                        <Button type="submit" :disabled="!agentId || isSavingUrl" :label="urlForm.reviewBeforeIndex ? 'Discover Pages' : 'Add URL'" class="form-submit" />
                    </form>

                    <form class="form-grid form-card" @submit.prevent="addManualText">
                        <div class="form-card-head">
                            <h3>Manual Text</h3>
                            <span class="form-card-tag">Snippet</span>
                        </div>
                        <div class="field"><label>Title</label><InputText v-model="textForm.title" required fluid /></div>
                        <div class="field"><label>Content</label><Textarea v-model="textForm.content" rows="6" required autoResize fluid /></div>
                        <Button type="submit" label="Add Text" :disabled="!agentId || isSavingText" class="form-submit" />
                    </form>
                </div>

                <form class="form-grid form-card" style="margin-top: 24px" @submit.prevent="uploadDocument">
                    <div class="form-card-head">
                        <h3>Document</h3>
                        <span class="form-card-tag">File</span>
                    </div>
                    <div class="field"><label>Title</label><InputText v-model="docTitle" fluid /></div>
                    <div class="field">
                        <label>File</label>
                        <label class="file-input-shell" for="kb-file-input">
                            <input id="kb-file-input" ref="fileInput" type="file" @change="onFileChange" />
                            <span class="file-input-button">Choose File</span>
                            <span class="file-input-name">{{ file?.name || "No file selected" }}</span>
                        </label>
                    </div>
                    <Button type="submit" label="Upload Document" :disabled="!agentId || !file || isUploadingDocument" class="form-submit" />
                </form>
            </section>

            <section class="panel">
                <div class="toolbar">
                    <h2>Sources</h2>
                    <div style="display: flex; gap: 8px; align-items: center">
                        <span class="badge">All {{ sources.length }}</span>
                        <span class="badge inactive">Web {{ webSourceCount }}</span>
                        <span class="badge inactive">Discovered {{ totalDiscoveredPages }}</span>
                        <span class="badge inactive">Indexed {{ totalIndexedPages }}</span>
                    </div>
                </div>

                <StateBlock v-if="!agentId" title="Choose an AI Agent" message="Select an agent to view and test its knowledge base crawl flow." />
                <StateBlock v-else-if="sources.length === 0" title="No sources yet" message="Add a URL, document, or manual text source for this AI Agent." />

                <div v-else class="table-container">
                    <table class="table">
                        <thead>
                            <tr>
                                <th>Source</th>
                                <th>State</th>
                                <th>Pages</th>
                                <th>Updated</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="source in sources" :key="source.id">
                                <tr style="cursor: pointer" @click="toggleSourceExpanded(source)">
                                    <td>
                                        <div style="font-weight: 600">{{ source.title }}</div>
                                        <div style="color: var(--text-secondary); font-size: 12px; word-break: break-all">{{ source.sourceType }}{{ source.url ? " - " + source.url : "" }}</div>
                                    </td>
                                    <td>
                                        <span class="badge" :class="{inactive: source.status !== 'Completed'}">{{ source.status }}</span>
                                    </td>
                                    <td style="color: var(--text-secondary)">
                                        <span v-if="source.sourceType === 'WebUrl'">{{ source.ingestedPageCount || 0 }} indexed / {{ source.discoveredPageCount || 0 }} discovered</span>
                                        <span v-else>{{ source.ingestedPageCount || 0 }} indexed</span>
                                    </td>
                                    <td style="color: var(--text-secondary)">{{ formatDate(source.updatedAt) }}</td>
                                    <td style="text-align: right">
                                        <Button size="small" severity="danger" text label="Delete" @click.stop="deleteSource(source)" />
                                    </td>
                                </tr>

                                <tr v-if="expandedSourceId === source.id" style="background: var(--bg-surface)">
                                    <td colspan="5" style="padding: 0">
                                        <div style="display: grid; gap: 20px; padding: 18px 24px; border-top: 1px solid var(--border-subtle)">
                                            <div v-if="source.sourceType === 'WebUrl'">
                                                <div class="toolbar compact">
                                                    <h2 style="font-size: 14px">Discovered Pages</h2>
                                                    <div style="display: flex; gap: 8px; align-items: center">
                                                        <InputText v-model="discoveredPageSearch" placeholder="Search title or URL" style="max-width: 260px" />
                                                        <Button
                                                            size="small"
                                                            severity="secondary"
                                                            outlined
                                                            :disabled="discoveredPages.length === 0"
                                                            label="Select all"
                                                            @click="selectAllDiscoveredPages(true)"
                                                        />
                                                        <Button
                                                            size="small"
                                                            severity="secondary"
                                                            outlined
                                                            :disabled="discoveredPages.length === 0"
                                                            label="Clear"
                                                            @click="selectAllDiscoveredPages(false)"
                                                        />
                                                        <Button
                                                            size="small"
                                                            :disabled="selectedDiscoveredPageCount === 0 || isIndexingSelectedPages"
                                                            :label="`Index selected (${selectedDiscoveredPageCount})`"
                                                            @click="indexSelectedDiscoveredPages(source)"
                                                        />
                                                    </div>
                                                </div>

                                                <div v-if="isLoadingDiscoveredPages" style="color: var(--text-secondary); font-size: 13px">Loading discovered pages...</div>
                                                <div v-else-if="discoveredPages.length === 0" style="color: var(--text-secondary); font-size: 13px">
                                                    No discovered pages for this source yet. Refresh after the DiscoverUrlPages job completes.
                                                </div>
                                                <div v-else-if="filteredDiscoveredPages.length === 0" style="color: var(--text-secondary); font-size: 13px">No discovered pages match your search.</div>
                                                <div v-else style="display: grid; gap: 8px">
                                                    <label
                                                        v-for="page in filteredDiscoveredPages"
                                                        :key="page.id"
                                                        style="
                                                            align-items: flex-start;
                                                            padding: 12px 14px;
                                                            background: var(--bg-panel);
                                                            border: 1px solid var(--border-subtle);
                                                            border-radius: var(--radius-sm);
                                                        "
                                                    >
                                                        <input v-model="page.isSelected" type="checkbox" />
                                                        <span style="display: grid; gap: 4px; min-width: 0">
                                                            <span style="font-weight: 600; color: var(--text-primary)">{{ page.title || "Untitled page" }}</span>
                                                            <span style="color: var(--text-secondary); font-size: 12px; word-break: break-all">{{ page.url }}</span>
                                                            <span style="color: var(--text-muted); font-size: 12px">
                                                                {{ page.indexedAt ? "Indexed " + formatDate(page.indexedAt) : page.isSelected ? "Selected for indexing" : "Discovered" }}
                                                            </span>
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>

                                            <div>
                                                <div class="toolbar compact">
                                                    <h2 style="font-size: 14px">Indexed Pages</h2>
                                                    <InputText v-model="ingestedPageSearch" placeholder="Search indexed pages" style="max-width: 260px" />
                                                </div>
                                                <div v-if="isLoadingPages" style="color: var(--text-secondary); font-size: 13px">Loading indexed pages...</div>
                                                <div v-else-if="ingestedPages.length === 0" style="color: var(--text-secondary); font-size: 13px">No indexed pages yet.</div>
                                                <div v-else-if="filteredIngestedPages.length === 0" style="color: var(--text-secondary); font-size: 13px">No indexed pages match your search.</div>
                                                <div v-else style="display: grid; gap: 8px">
                                                    <div
                                                        v-for="page in filteredIngestedPages"
                                                        :key="page.id"
                                                        style="padding: 12px 14px; background: var(--bg-panel); border: 1px solid var(--border-subtle); border-radius: var(--radius-sm)"
                                                    >
                                                        <div style="font-weight: 600">{{ page.title || "Untitled page" }}</div>
                                                        <div style="color: var(--text-secondary); font-size: 12px; word-break: break-all">{{ page.url || source.title }}</div>
                                                        <div style="color: var(--text-muted); font-size: 12px">{{ page.chunkCount }} chunks - {{ formatDate(page.createdAt) }}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
            </section>

            <section class="panel">
                <div class="toolbar">
                    <h2>Processing Jobs</h2>
                    <Button label="Refresh jobs" severity="secondary" outlined :disabled="isLoadingJobs" @click="loadJobs" />
                </div>
                <StateBlock v-if="jobs.length === 0" title="No jobs yet" message="Jobs will appear after adding or indexing a knowledge source." />
                <div v-else class="table-container">
                    <table class="table">
                        <thead>
                            <tr>
                                <th>Job</th>
                                <th>Status</th>
                                <th>Source</th>
                                <th>Created</th>
                                <th>Error</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="job in jobs" :key="job.id">
                                <td>{{ job.jobType }}</td>
                                <td>
                                    <span class="badge" :class="{inactive: job.status !== 'Completed'}">{{ job.status }}</span>
                                </td>
                                <td style="color: var(--text-secondary)">{{ sourceTitle(job.knowledgeBaseSourceId) }}</td>
                                <td style="color: var(--text-secondary)">{{ formatDate(job.createdAt) }}</td>
                                <td style="color: var(--danger); max-width: 320px">{{ job.errorMessage || "" }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    </DashboardLayout>
</template>
